/**
 * Analytics utility — pushes events to GTM dataLayer.
 * All SaaS metrics tracked here. GTM forwards to GA4.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function push(event: string, params: Record<string, unknown> = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export const analytics = {
  employerSignUp: () =>
    push('employer_sign_up', { user_role: 'employer' }),

  candidateSignUp: () =>
    push('candidate_sign_up', { user_role: 'candidate' }),

  employerLogin: () =>
    push('employer_login', { user_role: 'employer' }),

  candidateLogin: () =>
    push('candidate_login', { user_role: 'candidate' }),

  // Call after login to attach role + tier to all subsequent events
  setUserProperties: (role: 'employer' | 'candidate', tier?: string) =>
    push('set_user_properties', { user_role: role, subscription_tier: tier ?? 'free' }),

  // ─── Employer actions ───────────────────────────────────────────────────────

  jobPosted: (jobTitle: string, department?: string) =>
    push('job_posted', { job_title: jobTitle, department }),

  interviewScheduled: (interviewType: string) =>
    push('interview_scheduled', { interview_type: interviewType }),

  candidateShortlisted: () =>
    push('candidate_shortlisted'),

  screeningStarted: () =>
    push('screening_started'),

  // ─── Subscription ────────────────────────────────────────────────────────

  subscriptionStarted: (plan: string) =>
    push('subscription_started', { plan_name: plan }),

  subscriptionCancelled: (plan: string) =>
    push('subscription_cancelled', { plan_name: plan }),

  trialStarted: (plan: string) =>
    push('trial_started', { plan_name: plan }),

  // ─── Candidate actions ──────────────────────────────────────────────────────

  jobApplied: (jobTitle: string, companyName?: string) =>
    push('job_applied', { job_title: jobTitle, company_name: companyName }),

  voiceInterviewCompleted: (durationSeconds?: number) =>
    push('voice_interview_completed', { duration_seconds: durationSeconds }),
};
